import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@speaking-coach/ui';
import { useEffect, useId, useRef } from 'react';
/** Stable per-option DOM id for an input's virtual listbox cursor. */
export function optionDomId(listId, value) {
    return `${listId}-option-${value}`;
}
export function useListboxId() {
    return `listbox-${useId().replace(/:/g, '')}`;
}
/**
 * Search input for an async listbox. Results may arrive after the popup opens,
 * so the caller owns `activeId` and deliberately resets it as rows change.
 */
export function ListboxSearch({ listId, value, onValueChange, activeId, optionIds, onActiveIdChange, onSelect, onDismiss, placeholder, label, classes = {}, }) {
    function step(delta) {
        if (optionIds.length === 0)
            return;
        const at = optionIds.indexOf(activeId);
        const next = at === -1
            ? delta > 0
                ? 0
                : optionIds.length - 1
            : Math.min(Math.max(at + delta, 0), optionIds.length - 1);
        onActiveIdChange(optionIds[next] ?? '');
    }
    function onKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                step(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                step(-1);
                break;
            case 'Home':
                event.preventDefault();
                onActiveIdChange(optionIds[0] ?? '');
                break;
            case 'End':
                event.preventDefault();
                onActiveIdChange(optionIds[optionIds.length - 1] ?? '');
                break;
            case 'Enter':
                event.preventDefault();
                if (activeId !== '')
                    onSelect(activeId);
                break;
            case 'Escape':
                event.preventDefault();
                onDismiss();
                break;
            default:
                break;
        }
    }
    return (_jsxs("div", { className: classes.root, children: [_jsx(Icon, { name: "search", className: classes.icon }), _jsx("input", { type: "text", role: "searchbox", "aria-label": label, "aria-controls": listId, "aria-activedescendant": activeId === '' ? undefined : optionDomId(listId, activeId), autoComplete: "off", value: value, placeholder: placeholder, className: classes.input, onChange: (event) => onValueChange(event.target.value), onKeyDown: onKeyDown })] }));
}
export function Listbox({ id, label, children, className, }) {
    return (_jsx("ul", { id: id, role: "listbox", "aria-label": label, className: className, children: children }));
}
export function ListboxOption({ listId, value, active, onSelect, onHover, disabled = false, children, className, }) {
    const ref = useRef(null);
    useEffect(() => {
        if (active)
            ref.current?.scrollIntoView({ block: 'nearest' });
    }, [active]);
    return (_jsx("li", { ref: ref, id: optionDomId(listId, value), role: "option", "aria-selected": active, "aria-disabled": disabled || undefined, onPointerMove: () => {
            if (!disabled)
                onHover();
        }, onMouseDown: (event) => {
            event.preventDefault();
            if (!disabled)
                onSelect();
        }, className: className, children: children }));
}
export function ListboxMessage({ children, className, }) {
    return (_jsx("li", { role: "presentation", className: className, children: children }));
}
//# sourceMappingURL=listbox.js.map